import { ArrowRightLeft } from 'lucide-react'
import FootballIcon from './icons/FootballIcon'
import type { MatchEvent } from '../types'
import './MatchEvents.css'

interface MatchEventsProps {
  events: MatchEvent[]
  /**
   * The timeline is still being written. Only changes the empty state: "no events
   * yet" during a match and "no event details for this match" after one are different claims, and
   * an empty list alone cannot tell them apart.
   */
  partial?: boolean
}

/**
 * The detail line under the player's name — the non-colour tell for a card, and the assist.
 *
 * Empty for a plain goal: the ball on the row already says what happened, and repeating "Goal" under
 * the scorer's name is a line that carries nothing.
 */
function detailLine(event: MatchEvent): string {
  if (event.type === 'card') {
    // Yellow and red are the identical 10×14 chip at row size, so colour alone cannot be what
    // separates them. This line is what does, and it is why a second yellow says so by name.
    return event.note ?? (event.cardType === 'red' ? 'Red card' : 'Yellow card')
  }
  if (event.type === 'substitution') {
    return event.playerOff ? `Off: ${event.playerOff}` : 'Substitution'
  }
  // "ass. B. Fernandes", "Penalty", or both — and nothing at all for an unassisted plain goal.
  return [event.note, event.assistBy ? `ass. ${event.assistBy}` : null].filter(Boolean).join(' · ')
}

function EventIcon({ event }: { event: MatchEvent }) {
  if (event.type === 'goal') {
    // An own goal's ball is red — it is what explains why a name on one side made
    // the number on the other side go up. Keyed off the flag, never off the "Own goal" note.
    return (
      <span
        className={`match-events__icon match-events__icon--goal${
          event.ownGoal ? ' match-events__icon--own-goal' : ''
        }`}
      >
        <FootballIcon />
      </span>
    )
  }
  if (event.type === 'substitution') {
    // Tertiary ink at the goal icon's box size: a substitution does not change the score and must
    // not compete with a goal for attention.
    return (
      <span className="match-events__icon match-events__icon--sub" aria-hidden="true">
        <ArrowRightLeft />
      </span>
    )
  }
  return (
    <span
      className={`match-events__card ${
        event.cardType === 'red' ? 'match-events__card--red' : 'match-events__card--yellow'
      }`}
    />
  )
}

/**
 * One event, in its own team's column. Nothing labels the side — which column it lands in is what
 * says whose it is, the same way the score either side of the rail does on the row above.
 */
function EventCell({ event }: { event: MatchEvent }) {
  const detail = detailLine(event)
  return (
    <>
      {event.team === 'away' && <EventIcon event={event} />}
      <span className="match-events__info">
        <span className="match-events__player">{event.player}</span>
        {detail !== '' && <span className="match-events__detail">{detail}</span>}
      </span>
      {event.team === 'home' && <EventIcon event={event} />}
    </>
  )
}

function MatchEvents({ events, partial = false }: MatchEventsProps) {
  if (events.length === 0) {
    return (
      <p className="match-events__empty">
        {partial ? 'No events yet.' : 'No event details for this match.'}
      </p>
    )
  }

  // Newest first: the row that matters during a match is the one that just happened, and it should
  // not walk further down the panel every time something else does. The server sends the timeline
  // in chronological order — that is the order it is stored and graded in — so the reversal is a
  // presentation choice made here, at the only place that renders it.
  const newestFirst = [...events].reverse()

  return (
    <div className="match-events">
      {newestFirst.map((event, index) => (
        <div key={index} className="match-events__row">
          <div className="match-events__side match-events__side--home">
            {event.team === 'home' && <EventCell event={event} />}
          </div>
          <span className="match-events__minute">{event.minute}'</span>
          <div className="match-events__side match-events__side--away">
            {event.team === 'away' && <EventCell event={event} />}
          </div>
        </div>
      ))}
    </div>
  )
}

export default MatchEvents
